import type { ApiResponse, RemediationLimits, RemediationMode, RemediationPauseChoice, RemediationStep, RemediationSwitch, RemediationWaitingOn } from '@evi/shared';
import { api } from './api';

/**
 * The remediation ladder in Admin (Warren, 28 Oct 2026, evening addendum item 1: "with a kill switch in Admin and limits
 * per charger"): the Api's views (apps/api routes/settings/remediation.ts). Super Admin only; the Api answers 403 to
 * anyone else and the sections then show nothing.
 */
export interface LadderSettings { enabled: boolean; steps: Record<RemediationSwitch, boolean>; keep_in_shadow: boolean; limits: RemediationLimits }
export interface LadderView {
  value: LadderSettings;
  max_limits: RemediationLimits;
  shadow: { started_at: string | null; live_at: string | null; b: RemediationMode; c: RemediationMode };
}
export interface LadderAction {
  id: string; charger_id: string; identity: string; location: string | null; episode_id: string | null; at: string; step: RemediationStep; mode: RemediationMode;
  trigger: string; command: string | null; payload: Record<string, unknown> | null; outcome: string; cleared: boolean | null; cleared_at: string | null;
}
export interface ChargerLadderView {
  episode: {
    _id: string; trigger: { kind: string; connector_id: number | null }; phase: string; step: RemediationStep | null; mode: RemediationMode | null;
    waiting_on: RemediationWaitingOn | null; is_open: boolean; opened_at: string; closed_at: string | null; close_reason: string | null; rungs: string[];
  } | null;
  waiting_on: RemediationWaitingOn | null;
  actions: LadderAction[];
  paused_until: string | null;
  limits: RemediationLimits;
  network_limits: RemediationLimits;
}

export const fetchLadder = () => api.get('settings/remediation', { retry: 0 }).json<ApiResponse<LadderView>>().then((r) => r.data);
export const saveLadder = (value: LadderSettings) => api.put('settings/remediation', { json: value }).json<ApiResponse<LadderView>>().then((r) => r.data);
export const fetchLadderActions = (q: { mode?: RemediationMode | ''; step?: RemediationStep | '' } = {}) => {
  const params = new URLSearchParams({ limit: '50', ...(q.mode ? { mode: q.mode } : {}), ...(q.step ? { step: q.step } : {}) });
  return api.get(`settings/remediation/actions?${params.toString()}`, { retry: 0 }).json<ApiResponse<{ rows: LadderAction[] }>>().then((r) => r.data.rows);
};
export const fetchChargerLadder = (chargerId: string) =>
  api.get(`chargers/${chargerId}/remediation`, { retry: 0 }).json<ApiResponse<ChargerLadderView>>().then((r) => r.data);
export const saveChargerLadder = (chargerId: string, body: { pause?: RemediationPauseChoice | 'resume'; limits?: RemediationLimits }) =>
  api.put(`chargers/${chargerId}/remediation`, { json: body }).json<ApiResponse<{ paused_until: string | null; limits: RemediationLimits }>>().then((r) => r.data);

export interface BreakerDoneView {
  location_name: string;
  chargers: Array<{ label: string; identity: string }>;
  step: string;
  state: 'open' | 'done' | 'back_online' | 'closed';
  done_at: string | null;
  done_by_you: boolean;
}
export const fetchBreakerDone = (token: string) => api.get(`breaker-done/${encodeURIComponent(token)}`, { retry: 0 }).json<ApiResponse<BreakerDoneView>>().then((r) => r.data);
export const pressBreakerDone = (token: string) => api.post(`breaker-done/${encodeURIComponent(token)}`).json<ApiResponse<BreakerDoneView>>().then((r) => r.data);

export { actionLine } from './remediationView';
